import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Input } from '@/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import AlertDialog from '@/components/users/management/common/dialogs/AlertDialog';

interface Customer {
  id: number;
  customer_name: string;
  customer_code: string;
}

interface InviteUserDialogWithCustomerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    emails: string[],
    role: 'admin' | 'user',
    permission: 'readwrite' | 'readonly',
    customerId?: number,
    authType?: 'password' | 'oauth',
    passwordType?: 'manual' | 'random',
    manualPassword?: string,
    sendEmail?: boolean,
    onProgress?: (current: number, total: number) => void
  ) => Promise<void>;
  loading?: boolean;
  customers?: Customer[];
  showCustomerSelect?: boolean;
}

const InviteUserDialogWithCustomer: React.FC<InviteUserDialogWithCustomerProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  customers = [],
  showCustomerSelect = false,
}) => {
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'user'>('user');
  const [invitePermission, setInvitePermission] = useState<'readwrite' | 'readonly'>('readwrite');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined);
  const [authType, setAuthType] = useState<'password' | 'oauth'>('password');
  const [passwordType, setPasswordType] = useState<'manual' | 'random'>('random');
  const [manualPassword, setManualPassword] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [_progress, setProgress] = useState({ current: 0, total: 0 });
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // 여러 구분자로 분리: 줄바꿈, 세미콜론, 쉼표
    const emails = inviteEmails
      .split(/[\n;,]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0 && email.includes('@')); // 이메일 형식 검증
    
    if (emails.length === 0) {
      return;
    }

    // 최대 10명 제한
    if (emails.length > 10) {
      setAlertTitle('알림');
      setAlertMessage('한 번에 최대 10명까지만 초대할 수 있습니다.');
      setAlertOpen(true);
      return;
    }

    // 수동 비밀번호 입력 검증
    if (authType === 'password' && passwordType === 'manual' && !manualPassword) {
      setAlertTitle('알림');
      setAlertMessage('비밀번호를 입력해주세요.');
      setAlertOpen(true);
      return;
    }

    setProgress({ current: 0, total: emails.length });

    const progressCallback = (current: number, total: number) => {
      setProgress({ current, total });
    };

    await onSubmit(
      emails,
      inviteRole,
      invitePermission,
      selectedCustomerId,
      authType,
      passwordType,
      manualPassword,
      sendEmail,
      progressCallback
    );
    setInviteEmails('');
    setInviteRole('user');
    setInvitePermission('readwrite');
    setSelectedCustomerId(undefined);
    setAuthType('password');
    setPasswordType('random');
    setManualPassword('');
    setSendEmail(true);
    setProgress({ current: 0, total: 0 });
  };

  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-[500px]" onClose={onClose}>
        <DialogHeader>
          <DialogTitle>📧 사용자 초대</DialogTitle>
          <DialogDescription>
            초대할 사용자의 이메일을 입력하세요 (줄바꿈, 세미콜론(;), 쉼표(,)로 구분 가능)
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {showCustomerSelect && customers.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="inviteCustomer">고객사 선택 *</Label>
              <Select
                value={selectedCustomerId ? String(selectedCustomerId) : ''}
                onValueChange={(value) => setSelectedCustomerId(value ? parseInt(value) : undefined)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="고객사를 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={String(customer.id)}>
                      {customer.customer_name} ({customer.customer_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="inviteEmails">이메일 주소</Label>
            <textarea
              id="inviteEmails"
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              placeholder="user1@example.com&#10;user2@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[120px] resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={5}
            />
            <p className="text-xs text-gray-500">
              여러 이메일을 초대하려면 줄바꿈, 세미콜론(;), 또는 쉼표(,)로 구분하여 입력하세요 (최대 10명)
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="inviteRole">역할</Label>
            <select
              id="inviteRole"
              value={inviteRole}
              onChange={(e) => {
                const newRole = e.target.value as 'admin' | 'user';
                setInviteRole(newRole);
                if (newRole === 'admin') {
                  setInvitePermission('readwrite');
                }
              }}
              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="user">일반 사용자</option>
              <option value="admin">관리자</option>
            </select>
          </div>
          {inviteRole === 'user' && (
            <div className="space-y-2">
              <Label htmlFor="invitePermission">사용자 권한</Label>
              <select
                id="invitePermission"
                value={invitePermission}
                onChange={(e) => setInvitePermission(e.target.value as 'readwrite' | 'readonly')}
                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="readwrite">읽기/쓰기</option>
                <option value="readonly">읽기 전용</option>
              </select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="authType">로그인 유형</Label>
            <select
              id="authType"
              value={authType}
              onChange={(e) => setAuthType(e.target.value as 'password' | 'oauth')}
              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="password">ID/PW 로그인</option>
              <option value="oauth">OAuth2 로그인 (Google 로그인)</option>
            </select>
          </div>
          {authType === 'password' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="passwordType">비밀번호 설정</Label>
                <select
                  id="passwordType"
                  value={passwordType}
                  onChange={(e) => setPasswordType(e.target.value as 'manual' | 'random')}
                  className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="random">랜덤 생성 (이메일로 발송)</option>
                  <option value="manual">수동 입력</option>
                </select>
              </div>
              {passwordType === 'manual' && (
                <div className="space-y-2">
                  <Label htmlFor="manualPassword">비밀번호 *</Label>
                  <Input
                    type="password"
                    id="manualPassword"
                    value={manualPassword}
                    onChange={(e) => setManualPassword(e.target.value)}
                    required
                    minLength={6}
                    maxLength={32}
                  />
                  <p className="text-xs text-gray-500">
                    최소 6자, 최대 32자. 대문자, 소문자, 숫자, 특수문자 중 2가지 이상 포함
                  </p>
                </div>
              )}
          <div className="flex items-center space-x-2 pt-2 border-t">
            <input
              id="sendEmail"
              type="checkbox"
              className="w-4 h-4"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <div className="flex flex-col">
              <Label htmlFor="sendEmail" className="cursor-pointer">
                초대 이메일 발송
              </Label>
              <p className="text-xs text-gray-500">
                체크 해제 시, 사용자는 생성되지만 초대 이메일은 발송되지 않습니다.
              </p>
            </div>
          </div>
            </>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              취소
            </Button>
            <Button type="submit" disabled={loading || !inviteEmails.trim() || (showCustomerSelect && !selectedCustomerId)}>
              {loading ? '초대 중...' : '초대하기'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        confirmText="확인"
        variant="default"
        onConfirm={() => setAlertOpen(false)}
      />
    </Dialog>
  );
};

export default InviteUserDialogWithCustomer;

